import { httpClient } from '../httpClient';

const PREFIX = '/api/calculator';

/**
 * GET /api/calculator/calculate/
 * @param {string} planId
 * @param {Record<string, unknown>} [extraParams] e.g. { calc_type: 'LQ' }
 */
export function fetchCalculatePayload(planId, extraParams = {}) {
  return httpClient
    .get(`${PREFIX}/calculate/`, {
      params: { plan_id: planId, ...extraParams },
    })
    .then((res) => res.data);
}

/**
 * POST /api/calculator/select_plan/
 * @param {string} planId
 */
export function selectPlan(planId) {
  return httpClient.post(`${PREFIX}/select_plan/`, { plan_id: planId });
}

/**
 * GET /api/calculator/history/run/:runId/
 * @param {string|number} runId
 */
export function fetchHistoryRunRecords(runId, axiosConfig = {}) {
  return httpClient
    .get(`${PREFIX}/history/run/${runId}/`, axiosConfig)
    .then((res) => res.data);
}

/**
 * POST /api/calculator/history/run/:runId/fork/
 * @param {string} runId
 */
export function forkHistoryRun(runId) {
  return httpClient
    .post(`${PREFIX}/history/run/${runId}/fork/`)
    .then((res) => res.data);
}

/**
 * GET /api/calculator/history/
 * @param {Record<string, unknown>} [params]
 */
export function fetchHistoryList(params = {}) {
  return httpClient
    .get(`${PREFIX}/history/`, { params })
    .then((res) => res.data);
}

/**
 * GET /api/calculator/history/:recordId/
 * @param {string|number} recordId
 */
export function fetchHistoryRecord(recordId, axiosConfig = {}) {
  return httpClient
    .get(`${PREFIX}/history/${recordId}/`, axiosConfig)
    .then((res) => res.data);
}

/**
 * DELETE /api/calculator/history/run/:runId/
 * @param {string|number} runId
 */
export function deleteHistoryRun(runId) {
  return httpClient.delete(`${PREFIX}/history/run/${runId}/`);
}

/**
 * DELETE /api/calculator/history/:recordId/
 * @param {string|number} recordId
 */
export function deleteHistoryRecord(recordId) {
  return httpClient.delete(`${PREFIX}/history/${recordId}/`);
}

/**
 * POST /api/calculator/get_model_params/
 * @param {string} modelType
 * @param {string} model
 * @param {number|string} structure
 * @param {string} planId
 */
export function fetchModelParametersApi(modelType, model, structure, planId) {
  return httpClient
    .post(`${PREFIX}/get_model_params/`, {
      model_type: modelType,
      model,
      structure,
      plan_id: planId,
    })
    .then((res) => res.data);
}

/**
 * POST /api/calculator/calculate_cp/
 * @param {object} body
 */
export function calculateCp(body) {
  return httpClient
    .post(`${PREFIX}/calculate_cp/`, body)
    .then((res) => res.data);
}

export function fetchLiteratureParameterSets(params) {
  return httpClient.get(`${PREFIX}/literature/parameter-sets/`, { params }).then((res) => res.data);
}

/**
 * POST /api/calculator/sigmoid_curves/
 * @param {{ run_ids: Array<string|number>, record_ids: Array<string|number>, options: object }} payload
 */
export function fetchSigmoidCurves(payload, axiosConfig = {}) {
  return httpClient
    .post(`${PREFIX}/sigmoid_curves/`, payload, axiosConfig)
    .then((res) => res.data);
}

/**
 * GET /api/calculator/plans/
 */
export function fetchPlans() {
  return httpClient
    .get(`${PREFIX}/plans/`)
    .then((res) => res.data);
}

/**
 * DELETE /api/calculator/plans/:planId/
 * @param {string} planId
 */
export function deletePlan(planId) {
  return httpClient.delete(`${PREFIX}/plans/${encodeURIComponent(planId)}/`);
}

/**
 * GET /api/calculator/check_dvh/
 * @param {string} planId
 */
export function checkDvh(planId) {
  return httpClient
    .get(`${PREFIX}/check_dvh/`, { params: { plan_id: planId } })
    .then((res) => res.data);
}

/**
 * GET /api/calculator/dvh_structures/
 * @param {string} planId
 */
export function fetchDvhStructures(planId) {
  return httpClient
    .get(`${PREFIX}/dvh_structures/`, { params: { plan_id: planId } })
    .then((res) => res.data);
}

/**
 * POST /api/calculator/dvh_structures/
 * @param {string} planId
 * @param {Array<{ original_name: string, name: string, role: string }>} structures
 */
export function confirmDvhStructures(planId, structures) {
  return httpClient
    .post(`${PREFIX}/dvh_structures/`, { plan_id: planId, structures })
    .then((res) => res.data);
}

/**
 * POST /api/calculator/upload_dvh/
 * @param {File} file
 * @param {(progressEvent: any) => void} [onUploadProgress]
 */
export function uploadDvh(file, onUploadProgress) {
  const formData = new FormData();
  formData.append('txtFile', file);
  return httpClient
    .post(`${PREFIX}/upload_dvh/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
    })
    .then((res) => res.data);
}

/**
 * GET /api/calculator/courses/
 */
export function fetchCourses() {
  return httpClient
    .get(`${PREFIX}/courses/`)
    .then((res) => res.data);
}

/**
 * POST /api/calculator/courses/
 * @param {{ course_name: string, localization: string, mode?: string }} payload
 */
export function createCourse(payload) {
  return httpClient
    .post(`${PREFIX}/courses/`, payload)
    .then((res) => res.data);
}

/**
 * DELETE /api/calculator/courses/:courseId/
 * @param {string|number} courseId
 */
export function deleteCourse(courseId) {
  return httpClient.delete(`${PREFIX}/courses/${courseId}/`);
}

/**
 * PATCH /api/calculator/history/:recordId/
 * @param {string|number} recordId
 * @param {object} payload
 */
export function patchHistoryRecord(recordId, payload) {
  return httpClient
    .patch(`${PREFIX}/history/${recordId}/`, payload)
    .then((res) => res.data);
}

/**
 * GET /api/calculator/calculate_mixed/
 */
export function fetchMixedCalculateData() {
  return httpClient
    .get(`${PREFIX}/calculate_mixed/`)
    .then((res) => res.data);
}

/**
 * POST /api/calculator/get_mixed_model_params/
 * @param {object} payload
 */
export function fetchMixedModelParams(payload) {
  return httpClient
    .post(`${PREFIX}/get_mixed_model_params/`, payload)
    .then((res) => res.data);
}

/**
 * POST /api/calculator/calculate_mixed_cp/
 * @param {object} payload
 */
export function calculateMixedCp(payload) {
  return httpClient
    .post(`${PREFIX}/calculate_mixed_cp/`, payload)
    .then((res) => res.data);
}

/**
 * POST /api/calculator/adaptive_rt/validate/
 */
export function validateAdaptiveRtStages(payload) {
  return httpClient
    .post(`${PREFIX}/adaptive_rt/validate/`, payload)
    .then((res) => res.data);
}

/**
 * POST /api/calculator/adaptive_rt/structures/
 */
export function fetchAdaptiveRtCommonStructures(payload) {
  return httpClient
    .post(`${PREFIX}/adaptive_rt/structures/`, payload)
    .then((res) => res.data);
}

/**
 * POST /api/calculator/get_adaptive_rt_model_params/
 */
export function fetchAdaptiveRtModelParams(payload) {
  return httpClient
    .post(`${PREFIX}/get_adaptive_rt_model_params/`, payload)
    .then((res) => res.data);
}

/**
 * POST /api/calculator/calculate_adaptive_rt_cp/
 */
export function calculateAdaptiveRtCp(payload) {
  return httpClient
    .post(`${PREFIX}/calculate_adaptive_rt_cp/`, payload)
    .then((res) => res.data);
}

/**
 * POST /api/calculator/report/export/
 * @param {{ format: 'pdf'|'docx', document: object }} payload
 */
export function exportReport(payload) {
  return httpClient.post(`${PREFIX}/report/export/`, payload, {
    responseType: 'blob',
  });
}
